import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "../engagement/FormControls";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { relationship: "Father", name: "", deceased: false, age_of_death: "",
                age_of_onset: "", cause_of_death: "", conditions: [], genetic_markers: "",
                environmental_exposures: "", ethnicity: "", living_with_condition: false,
                notes: "" };

export default function FamilyHealthHistory({ client, editable, dict }) {
  const relations = dict?.family_relations || ["Father", "Mother", "Brother", "Sister"];
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const load = () => api.get(`/clients/${client.id}/family-history`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line
  const filtered = useMemo(() => items.filter((x) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [x.relationship, x.name].some((v) => (v || "").toLowerCase().includes(s))
      || (x.conditions || []).some((c) => (c.condition || "").toLowerCase().includes(s));
  }), [items, q]);

  const addCondition = () => setForm((s) => ({ ...s, conditions: [...s.conditions, { condition: "", age_of_onset: "", icd10_code: "" }] }));
  const updateCondition = (i, k, v) => setForm((s) => ({ ...s, conditions: s.conditions.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const removeCondition = (i) => setForm((s) => ({ ...s, conditions: s.conditions.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.relationship) return toast.error("Relationship is required");
    const conds = (form.conditions || []).filter((c) => (c.condition || "").trim());
    try {
      if (editId) await api.put(`/family-history/${editId}`, { ...form, conditions: conds });
      else await api.post(`/clients/${client.id}/family-history`, { ...form, conditions: conds });
      toast.success(editId ? "Family history updated" : "Family history added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/family-history/${del.id}`); toast.success("Family history removed"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="family-history-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#3E2723]">All Family Health Members</h3>
          <p className="text-xs text-[#7B6157]">{filtered.length} member{filtered.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="family-filter" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="rounded-full border border-[#E5DFD3] bg-white px-3.5 py-1.5 text-xs outline-none w-44 focus:border-[#8D6E63]" />
          {editable && <Btn data-testid="add-family-btn" variant="outline"
            onClick={() => { setForm(EMPTY); setEditId(null); setPanel(true); }}><Plus size={14} /> Add New Family Member</Btn>}
        </div>
      </div>

      {filtered.length === 0 ? <Empty text="No family health history on file" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((m) => (
            <div key={m.id} data-testid={`family-card-${m.id}`}
              className="rounded-2xl border border-[#EFEBE0] bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase text-[#7B6157]">{m.relationship}</div>
                  <div className="font-heading text-base font-bold text-[#3E2723]">{m.name || "(name not provided)"}</div>
                  {m.deceased && (
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">
                      Deceased{m.age_of_death ? ` at ${m.age_of_death}` : ""}{m.cause_of_death ? ` · ${m.cause_of_death}` : ""}
                    </div>
                  )}
                </div>
                {editable && (
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`edit-family-${m.id}`} onClick={() => { setForm({ ...EMPTY, ...m, conditions: m.conditions || [] }); setEditId(m.id); setPanel(true); }}
                      className="h-8 w-8 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                    <button data-testid={`delete-family-${m.id}`} onClick={() => setDel(m)}
                      className="h-8 w-8 rounded-full flex items-center justify-center text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
              {m.conditions && m.conditions.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-[10px] font-bold uppercase text-[#7B6157]">Conditions</div>
                  {m.conditions.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="rounded-lg bg-[#F5F2EB] px-2 py-1 font-semibold text-[#4E342E]">{c.condition}</span>
                      {c.icd10_code && <span className="font-mono text-[#7B6157]">{c.icd10_code}</span>}
                      {c.age_of_onset && <span className="text-[#7B6157]">Onset age {c.age_of_onset}</span>}
                    </div>
                  ))}
                </div>
              )}
              {m.notes && <p className="mt-2 text-xs text-[#7B6157]">{m.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Family Member" : "Add New Family Member"} testId="family-slide-panel">
        <form onSubmit={submit} className="space-y-4 p-1">
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Relationship *</FLabel><FSelect data-testid="family-relationship" value={form.relationship} onChange={f("relationship")}>{relations.map((r) => <option key={r}>{r}</option>)}</FSelect></div>
            <div><FLabel>Name (optional)</FLabel><FInput data-testid="family-name" value={form.name} onChange={f("name")} placeholder="First or first initial only" /></div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[#5D4037]">
            <input type="checkbox" data-testid="family-deceased" checked={!!form.deceased}
              onChange={(e) => setForm((s) => ({ ...s, deceased: e.target.checked }))} />
            Deceased
          </label>
          {form.deceased && (
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Age of Death</FLabel><FInput data-testid="family-age-death" value={form.age_of_death} onChange={f("age_of_death")} placeholder="72" /></div>
              <div><FLabel>Cause of Death</FLabel><FInput data-testid="family-cause-death" value={form.cause_of_death} onChange={f("cause_of_death")} placeholder="Cardiac arrest" /></div>
            </div>
          )}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase text-[#7B6157]">Health Conditions</div>
              <button type="button" data-testid="family-add-condition" onClick={addCondition}
                className="rounded-full border border-[#E5DFD3] px-2.5 py-1 text-[11px] font-bold text-[#4E342E] hover:bg-[#F5F2EB]"><Plus size={11} className="inline mr-1" />Add condition</button>
            </div>
            {form.conditions.length === 0 ? <div className="rounded-xl bg-[#FDFCF8] p-3 text-xs text-[#7B6157]">No conditions listed.</div> : (
              <div className="space-y-2">
                {form.conditions.map((c, i) => (
                  <div key={i} data-testid={`family-condition-${i}`} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5"><FLabel>Condition</FLabel><FInput data-testid={`family-condition-name-${i}`} value={c.condition} onChange={(e) => updateCondition(i, "condition", e.target.value)} placeholder="Depression" /></div>
                    <div className="col-span-3"><FLabel>ICD-10</FLabel><FInput data-testid={`family-condition-icd-${i}`} value={c.icd10_code || ""} onChange={(e) => updateCondition(i, "icd10_code", e.target.value)} /></div>
                    <div className="col-span-3"><FLabel>Age of Onset</FLabel><FInput data-testid={`family-condition-onset-${i}`} value={c.age_of_onset || ""} onChange={(e) => updateCondition(i, "age_of_onset", e.target.value)} /></div>
                    <button type="button" data-testid={`family-condition-remove-${i}`} onClick={() => removeCondition(i)}
                      className="col-span-1 h-10 rounded-lg text-[#C62828] hover:bg-[#FFEBEE] flex items-center justify-center"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Age of Onset</FLabel><FInput data-testid="family-age-onset" value={form.age_of_onset} onChange={f("age_of_onset")} placeholder="e.g., 45" /></div>
            <div><FLabel>Ethnicity</FLabel><FInput data-testid="family-ethnicity" value={form.ethnicity} onChange={f("ethnicity")} placeholder="e.g., Ashkenazi Jewish" /></div>
            <div className="col-span-2"><FLabel>Genetic Markers</FLabel><FInput data-testid="family-genetic-markers" value={form.genetic_markers} onChange={f("genetic_markers")} placeholder="e.g., BRCA1, BRCA2, APOE4, Lynch syndrome" /></div>
            <div className="col-span-2"><FLabel>Environmental Exposures</FLabel><FInput data-testid="family-env-exposures" value={form.environmental_exposures} onChange={f("environmental_exposures")} placeholder="e.g., Occupational asbestos, agricultural pesticides" /></div>
          </div>
          <div><FLabel>Notes</FLabel><FTextarea data-testid="family-notes" rows={2} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="family-save" type="submit">{editId ? "Save Changes" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={remove}
        title="Remove family member" description={`Delete ${del?.relationship}${del?.name ? ` (${del.name})` : ""}?`} />
    </div>
  );
}
